import { useState } from "react";
import { Mail, Check, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export function HeaderSubscribe() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
  const [notice, setNotice] = useState("");
  const [open, setOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || status === "loading") return;

    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: "Invalid email", variant: "destructive" });
      return;
    }

    setStatus("loading");
    try {
      // New subscribers are saved unconfirmed — they only get blog emails after
      // clicking the link in the confirmation email.
      const { error } = await supabase
        .from("blog_subscribers")
        .insert({ email: trimmed, is_confirmed: false, subscribed_at: new Date().toISOString() });

      // Duplicate email = already on the list; we still (re)send the confirmation
      if (error && error.code !== "23505") throw error;

      const { data, error: sendError } = await supabase.functions.invoke(
        "send-confirmation-email",
        { body: { email: trimmed } }
      );

      if (sendError) throw sendError;

      const already = Boolean(data?.already);
      setNotice(
        already
          ? "You're already on the list — nothing else to do."
          : "We've emailed you a link. Click it to start receiving posts."
      );
      setStatus("success");
      setEmail("");
      toast({
        title: already ? "Already subscribed" : "Check your inbox",
        description: already
          ? "You're already on the list."
          : "We sent a confirmation link to your email.",
      });
      setTimeout(() => {
        setStatus("idle");
        setOpen(false);
        setNotice("");
      }, 4000);
    } catch (err: any) {
      console.error("Subscribe error:", err);
      toast({ title: "Error subscribing", description: err.message, variant: "destructive" });
      setStatus("idle");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <Mail className="w-3.5 h-3.5" />
          <span>Subscribe</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 overflow-hidden">
        {status === "success" ? (
          <div className="flex flex-col items-center justify-center py-8 px-6 text-center">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <Check className="w-5 h-5 text-primary" />
            </div>
            <p className="font-semibold text-sm">
              {notice.startsWith("You're already") ? "You're subscribed!" : "Check your inbox!"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {notice || "We'll notify you when new posts are published."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="bg-primary/5 px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm">Stay in the loop</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                Get notified when I publish new blog posts.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-9 text-sm"
                disabled={status === "loading"}
                autoFocus
              />
              <Button
                type="submit"
                className="w-full h-9 text-sm gap-2"
                disabled={status === "loading" || !email.trim()}
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    Subscribe
                  </>
                )}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                We'll email a quick confirmation link first. No spam, ever.
              </p>
            </form>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
